import {
  Form,
  FormInput,
  FormField,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  FormSelect,
  Button,
  FormDatePicker,
  FormPrefixedInput,
  V2Dialog,
  DialogContent,
  Switch,
  cn,
} from "web-design-system";
import {
  ChevronDownIcon,
  ChevronRight,
  Settings2,
  ShieldCheck,
  User,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  SelfDeclarationForm,
  SIGNUP_DATE_CUTOFF,
} from "@core/components/self-declaration-form";
import { Route } from "@/routes/(auth)/_headed/account/setting";
import {
  DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS,
  NOMINEE_RELATIONSHIP,
} from "creso-core/constants";
import {
  useCreateDistributorBankDetails,
  useUpdateDistributorBankDetails,
  useUpdateDistributorPreferences,
  useUpdateDistributorProfile,
  useUpsertDistributorNominee,
} from "creso-core/hooks";
import { z } from "zod";
import { accountNumberSchema, gstSchema, ifscCodeSchema } from "creso-core/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { useAuthContext } from "@/providers/auth-provider";
import { endOfDay, isAfter } from "date-fns";

const distributorSettingsSchema = z.object({
  name: z.string(),
  arn_code: z.string(),
  mobile: z.string(),
  email: z.string(),
  euin: z
    .string()
    .nullable()
    .default("")
    .refine((val) => !val || /^\d{6}$/.test(val), {
      message: "EUIN must be exactly 6 digits",
    }),
  account_number: z.string().default(""), // accountNumberSchema,
  confirm_account_number: z.string().default(""),
  ifsc_code: z.string().default(""), // ifscCodeSchema,
  account_type: z.string().default(""),
  nominee_name: z.string().default(""),
  nominee_relation: z.string().default(""),
  nominee_pan: z.string().default(""),
  nominee_dob: z.string().default(""), // dobSchema.optional(),
  gst_number: z.union([z.literal(""), gstSchema]).nullable(),
  idcw_payout_enabled: z.boolean().default(false),
});

export function Setting() {
  const { profile, banks, nominee, preferences } = Route.useLoaderData();

  const [declarationOpen, setDeclarationOpen] = useState(false);
  const isDeclarationSubmitted = Boolean(profile?.declaration_link);

  const form = useForm({
    resolver: zodResolver(distributorSettingsSchema),
    values: {
      name: profile?.name || "",
      arn_code: profile?.arn_code || "",
      mobile: profile?.phone_number || "",
      email: profile?.email || "",
      account_number: banks?.account_number || "",
      confirm_account_number: "",
      ifsc_code: banks?.ifsc_code || "",
      account_type: banks?.account_type || "",
      nominee_name: nominee?.name || "",
      nominee_relation: nominee?.relationship || "",
      nominee_pan: nominee?.pan_card || "",
      nominee_dob: nominee?.date_of_birth || "",
      euin: (profile?.euin || "")
        .replace(/^E/, "")
        .replace(/\D/g, "")
        .slice(0, 6),
      gst_number: profile?.gst_number || "",
      idcw_payout_enabled: preferences?.idcw_payout_enabled ?? false,
    },
  });

  const {
    formState: { isDirty, dirtyFields, isSubmitting },
  } = form;

  const { mutateAsync: updateProfileDetails } = useUpdateDistributorProfile();
  const { mutateAsync: createBankDetails } = useCreateDistributorBankDetails();
  const { mutateAsync: upsertNomineeDetails } = useUpsertDistributorNominee();
  const { mutateAsync: updateBankDetails } = useUpdateDistributorBankDetails();
  const { mutateAsync: updatePreferences } = useUpdateDistributorPreferences();

  const router = useRouter();

  const onSubmit = async (data: z.infer<typeof distributorSettingsSchema>) => {
    const isBankDetailsDirty =
      dirtyFields.account_number ||
      dirtyFields.ifsc_code ||
      dirtyFields.account_type;

    const isNomineeDetailsDirty =
      dirtyFields.nominee_name ||
      dirtyFields.nominee_relation ||
      dirtyFields.nominee_pan ||
      dirtyFields.nominee_dob;

    const isProfileDetailsDirty = dirtyFields.euin || dirtyFields.gst_number;

    const isPreferencesDirty = dirtyFields.idcw_payout_enabled;

    const updatePromises = [];

    if (isPreferencesDirty) {
      updatePromises.push(
        updatePreferences({ idcw_payout_enabled: data.idcw_payout_enabled })
      );
    }

    if (isProfileDetailsDirty) {
      const euinToSend = data.euin
        ? data.euin.startsWith("E")
          ? data.euin
          : `E${data.euin}`
        : "";
      updatePromises.push(
        updateProfileDetails({
          euin: euinToSend,
          gst_number: data.gst_number ?? "",
        })
      );
    }

    let hasErrors = false;
    if (isBankDetailsDirty) {
      if (!accountNumberSchema.safeParse(data.account_number).success) {
        form.setError("account_number", {
          message: "Invalid account number",
        });
        hasErrors = true;
      }
      const accountNumberDirty = dirtyFields.account_number;
      if (accountNumberDirty) {
        if (data.account_number !== data.confirm_account_number) {
          form.setError("confirm_account_number", {
            message: "Account numbers do not match",
          });
          hasErrors = true;
        }
      }
      if (!ifscCodeSchema.safeParse(data.ifsc_code).success) {
        form.setError("ifsc_code", {
          message: "Invalid IFSC code",
        });
        hasErrors = true;
      }

      if (!data.account_type) {
        form.setError("account_type", {
          message: "Please select an account type",
        });
        hasErrors = true;
      }
    }

    if (isNomineeDetailsDirty) {
      if (!data.nominee_name) {
        form.setError("nominee_name", {
          message: "Name is required",
        });
        hasErrors = true;
      }
      if (!data.nominee_relation) {
        form.setError("nominee_relation", {
          message: "Relation is required",
        });
        hasErrors = true;
      }
    }

    if (hasErrors) return;

    if (isBankDetailsDirty) {
      updatePromises.push(
        banks?.id
          ? updateBankDetails({
              account_number: data.account_number,
              ifsc_code: data.ifsc_code,
              account_type: data.account_type,
            })
          : createBankDetails({
              account_number: data.account_number,
              ifsc_code: data.ifsc_code,
              account_type: data.account_type,
            })
      );
    }

    if (isNomineeDetailsDirty) {
      updatePromises.push(
        upsertNomineeDetails({
          name: data.nominee_name,
          relationship: data.nominee_relation,
          pan_card: data.nominee_pan,
          date_of_birth: data.nominee_dob,
        })
      );
    }

    const result = await Promise.allSettled(updatePromises);

    const errors = result.filter((r) => r.status === "rejected");

    if (errors.length > 0) {
      toast.error("Failed to update profile");
    } else {
      toast.success("Profile updated successfully");
    }

    router.invalidate({ sync: true });
  };

  const { user } = useAuthContext();

  const showSelfDeclarationBanner = useMemo(() => {
    if (user.user_type !== "distributor") return false;
    if (isAfter(new Date(user.date_joined), endOfDay(SIGNUP_DATE_CUTOFF)))
      return false;
    return true;
  }, [user]);

  return (
    <div className="p-5 w-full flex flex-col h-full max-w-5xl">
      {profile && showSelfDeclarationBanner && (
        <SelfDeclarationBanner
          submitted={isDeclarationSubmitted}
          declarationLink={profile.declaration_link}
          onClick={() => setDeclarationOpen(true)}
        />
      )}

      {profile && showSelfDeclarationBanner && (
        <V2Dialog
          open={declarationOpen}
          onOpenChange={(v) => {
            setDeclarationOpen(v);
            if (!v) {
              router.invalidate();
            }
          }}
        >
          <DialogContent className="max-w-lg p-0 gap-0 max-h-[90dvh] flex flex-col overflow-hidden">
            <div className="overflow-y-auto px-6 pt-6">
              <SelfDeclarationForm
                details={{
                  name: profile.name,
                  arnCode: profile.arn_code,
                  phoneNumber: profile.phone_number,
                  email: profile.email,
                }}
                otpVariant="inline"
                mode={isDeclarationSubmitted ? "review" : "submit"}
                declarationLink={profile.declaration_link}
                onClose={() => {
                  setDeclarationOpen(false);
                }}
              />
            </div>
          </DialogContent>
        </V2Dialog>
      )}

      <div className="flex items-center gap-2">
        <User className="size-4" />
        <span className="text-lg font-semibold">Account</span>
      </div>
      <div className="h-px w-full bg-border mt-3" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col flex-1"
        >
          <Collapsible
            defaultOpen
            className="group border-b data-[state=open]:pb-5 border-border"
          >
            <CollapsibleTrigger className="w-full py-5 flex items-center justify-between">
              <h6>Personal Information</h6>
              <ChevronDownIcon className="size-4 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-6">
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormInput {...field} label="Name" disabled />
                  )}
                />
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="arn_code"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="AMFI registration number"
                      disabled
                    />
                  )}
                />
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="mobile"
                  render={({ field }) => (
                    <FormInput {...field} label="Mobile number" disabled />
                  )}
                />
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormInput {...field} label="Email" disabled />
                  )}
                />
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="euin"
                  render={({ field }) => (
                    <FormPrefixedInput
                      {...field}
                      placeholder="123456"
                      value={String(field.value ?? "")
                        .replace(/\D/g, "")
                        .slice(0, 6)}
                      onChange={(val) =>
                        field.onChange(
                          String(val ?? "")
                            .replace(/\D/g, "")
                            .slice(0, 6)
                        )
                      }
                      maxLength={6}
                      onKeyDown={(event) => {
                        if (
                          !event.ctrlKey &&
                          !event.metaKey &&
                          !event.altKey &&
                          event.key.length === 1 &&
                          !/\d/.test(event.key)
                        ) {
                          event.preventDefault();
                        }
                      }}
                      label="EUIN"
                      prefix=""
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    >
                      E
                    </FormPrefixedInput>
                  )}
                />
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="gst_number"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ""}
                      type="text"
                      label="GSTIN"
                      autoComplete="off"
                    />
                  )}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible className="group border-b data-[state=open]:pb-5 border-border">
            <CollapsibleTrigger className="w-full py-5 flex items-center justify-between">
              <h6>Bank details</h6>
              <ChevronDownIcon className="size-4 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>

            <CollapsibleContent className="flex flex-col gap-6">
              <div className="flex gap-6">
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="account_number"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      withEye
                      label="Account number"
                      type="password"
                      value={field.value ?? ""}
                      autoComplete="new-password"
                    />
                  )}
                />
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="confirm_account_number"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      disabled={!dirtyFields.account_number}
                      label="Confirm account number"
                      value={field.value ?? ""}
                    />
                  )}
                />
              </div>

              <div className="flex gap-6">
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="ifsc_code"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="IFSC Code"
                      value={field.value ?? ""}
                    />
                  )}
                />
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="account_type"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Account type"
                      options={DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS}
                    />
                  )}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible className="group border-b data-[state=open]:pb-5 border-border">
            <CollapsibleTrigger className="w-full py-5 flex items-center justify-between">
              <h6>Nominee</h6>
              <ChevronDownIcon className="size-4 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>

            <CollapsibleContent className="flex flex-col gap-6">
              <div className="flex gap-6">
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="nominee_name"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Name"
                      value={field.value ?? ""}
                    />
                  )}
                />
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="nominee_relation"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Relation"
                      options={NOMINEE_RELATIONSHIP}
                    />
                  )}
                />
              </div>
              <div className="flex gap-6">
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="nominee_pan"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="PAN"
                      value={field.value ?? ""}
                    />
                  )}
                />
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="nominee_dob"
                  render={({ field }) => (
                    <FormDatePicker
                      label="Date of Birth"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select Date of Birth"
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date > today;
                      }}
                    />
                  )}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="border-b border-border pb-5">
            <div className="flex items-center gap-2 py-5">
              <Settings2 className="size-4" />
              <span className="text-lg font-semibold">Preferences</span>
            </div>
            <FormField
              control={form.control}
              name="idcw_payout_enabled"
              render={({ field }) => (
                <div className="flex items-center justify-between gap-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-md text-foreground">
                      Transactions in IDCW funds
                    </span>
                    <span className="text-sm text-muted-foreground">
                      You will be shown IDCW funds while creating transactions.
                    </span>
                  </div>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex flex-row gap-5 mt-auto items-center justify-end sticky left-0 w-full bg-background py-5">
            <Button
              type="reset"
              onClick={() => form.reset()}
              disabled={!isDirty}
              variant="outline"
            >
              Reset
            </Button>
            <Button disabled={isSubmitting || !isDirty} type="submit">
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

/**
 * Submission date is encoded as the trailing `YYYY-MM-DD` of the declaration
 * PDF file name, e.g. `777777_declaration_2026-2027_2026-06-06.pdf`.
 */
function getDeclarationDate(declarationLink?: string | null) {
  if (!declarationLink) return null;
  const match = declarationLink.match(/(\d{4}-\d{2}-\d{2})(?=\.pdf)/);
  if (!match) return null;
  const date = new Date(match[1]);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function SelfDeclarationBanner({
  submitted,
  declarationLink,
  onClick,
}: {
  submitted: boolean;
  declarationLink?: string | null;
  onClick: () => void;
}) {
  const formattedDate = getDeclarationDate(declarationLink);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group mb-5 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors",
        submitted
          ? "bg-tag-green hover:bg-tag-green/60"
          : "bg-tag-yellow hover:bg-tag-yellow/60"
      )}
    >
      <ShieldCheck
        className={cn(
          "size-4 shrink-0",
          submitted ? "text-tag-green-foreground" : "text-tag-yellow-foreground"
        )}
      />
      <p className="flex-1 text-sm">
        <span className="font-semibold">
          Annual Declaration of Self-Certification
        </span>
        <span className="text-muted-foreground">
          {" · "}
          {submitted
            ? `Submitted for FY 2025-2026${formattedDate ? ` on ${formattedDate}` : ""}`
            : "Pending submission for FY 2025-2026 (due 30 Jun 2026)"}
        </span>
      </p>
      <span
        className={cn(
          "flex shrink-0 items-center gap-0.5 text-sm font-semibold",
          submitted ? "text-tag-green-foreground" : "text-tag-yellow-foreground"
        )}
      >
        {submitted ? "Know more" : "Submit declaration"}
        <ChevronRight className="size-4" />
      </span>
    </button>
  );
}
